import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import { toast } from 'react-toastify';
import Async from 'react-select/async';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { DashboardProvider, useDashboard } from '../../components/CustomDashboard/contexts/DashboardContext';
import { ReactComponent as CopyIcon } from '../../assets/copy-new.svg';
import { ReactComponent as EditIcon } from '../../assets/edit-new.svg';
import { ReactComponent as DeleteIcon } from '../../assets/delete-new.svg';
import { Loader } from '../../components/Loader/Loader';
import { Paginator } from '../../components/Paginator/Paginator';
import { Dashboard } from '../../components/CustomDashboard/types';
import Modal from '../../components/Modal';
import { API } from '../../API';
import styles from './DashboardsListPage.module.scss';

interface WorkspaceOption {
  value: number;
  label: string;
}

const PAGE_SIZE = 10;

const DashboardsListInner: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentUser] = useCurrentUser();
  const {
    dashboards,
    totalDashboards,
    currentPage,
    isLoading,
    fetchDashboards,
    createDashboard,
    updateDashboardMeta,
    deleteDashboard,
    copyDashboard,
  } = useDashboard();
  const isSuperAdmin = currentUser?.is_super_admin ?? false;

  const [formModal, setFormModal] = useState<{
    isActive: boolean;
    dashboard: Dashboard | null;
    sourceId: string | null;
  }>({ isActive: false, dashboard: null, sourceId: null });
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [targetWorkspace, setTargetWorkspace] = useState<WorkspaceOption | null>(null);
  const [defaultWorkspaceOptions, setDefaultWorkspaceOptions] = useState<WorkspaceOption[]>([]);
  const workspaceSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{
    isActive: boolean;
    id: string | null;
  }>({ isActive: false, id: null });

  useEffect(() => {
    fetchDashboards(1, PAGE_SIZE);
  }, [fetchDashboards]);

  const isCopyMode = !!formModal.sourceId;

  useEffect(() => {
    if (!isCopyMode || !isSuperAdmin) return;
    API.fetch('GET', '/API/v1/workspaces?size=100').then((data: any) => {
      const options = (data?.objects ?? [])
        .sort((a: any, b: any) => (a.name > b.name ? 1 : -1))
        .map((ws: { id: number; name: string }) => ({ value: ws.id, label: ws.name }));
      setDefaultWorkspaceOptions(options);
    });
  }, [isCopyMode, isSuperAdmin]);

  const loadWorkspaceOptions = useCallback((inputValue: string): Promise<WorkspaceOption[]> => {
    if (workspaceSearchTimer.current) {
      clearTimeout(workspaceSearchTimer.current);
    }
    if (!inputValue) {
      return Promise.resolve([]);
    }
    return new Promise((resolve) => {
      workspaceSearchTimer.current = setTimeout(() => {
        API.fetch('GET', `/API/v1/workspaces?q=${encodeURIComponent(inputValue)}`).then((data: any) => {
          resolve(
            (data?.objects ?? [])
              .sort((a: any, b: any) => (a.name > b.name ? 1 : -1))
              .map((ws: { id: number; name: string }) => ({ value: ws.id, label: ws.name })),
          );
        });
      }, 1000);
    });
  }, []);

  const handlePageChange = useCallback(
    (page: number) => {
      fetchDashboards(page, PAGE_SIZE);
    },
    [fetchDashboards]
  );

  const openCreateModal = () => {
    setFormTitle('');
    setFormDescription('');
    setFormModal({ isActive: true, dashboard: null, sourceId: null });
  };

  const openEditModal = (dashboard: Dashboard) => {
    setFormTitle(dashboard.title);
    setFormDescription(dashboard.description || '');
    setFormModal({ isActive: true, dashboard, sourceId: null });
  };

  const openCopyModal = (dashboard: Dashboard) => {
    setFormTitle(`${dashboard.title} (copy)`);
    setFormDescription(dashboard.description || '');
    setTargetWorkspace(null);
    setFormModal({ isActive: true, dashboard: null, sourceId: dashboard.id });
  };

  const closeFormModal = () => {
    setFormModal({ isActive: false, dashboard: null, sourceId: null });
    setFormTitle('');
    setFormDescription('');
    setTargetWorkspace(null);
  };

  const handleFormSubmit = async () => {
    if (!formTitle.trim()) return;
    if (formModal.dashboard) {
      await updateDashboardMeta(formModal.dashboard.id, {
        title: formTitle.trim(),
        description: formDescription.trim(),
      });
      fetchDashboards(currentPage, PAGE_SIZE);
    } else if (formModal.sourceId) {
      const dashboard = await copyDashboard(
        formModal.sourceId,
        formTitle.trim(),
        targetWorkspace?.value,
      );
      toast.success(t('Dashboard copied'));
      navigate(`/custom-dashboards/${dashboard.id}`);
    } else {
      const dashboard = await createDashboard({
        title: formTitle.trim(),
        description: formDescription.trim(),
      });
      navigate(`/custom-dashboards/${dashboard.id}`);
    }
    closeFormModal();
  };

  const handleDelete = async (id: string | null) => {
    if (!id) return;
    await deleteDashboard(id);
    setDeleteConfirmModal({ isActive: false, id: null });
    fetchDashboards(currentPage, PAGE_SIZE);
  };

  if (isLoading) {
    return <Loader />;
  }

  const isEditMode = !!formModal.dashboard;

  return (
    <div className="list-content">
      <div className="page-header">
        <div className="breadcrumb">
          <span>{t('Dashboards')}</span>
        </div>
        {isSuperAdmin && (
          <div className="controls">
            <button
              className="button new-button"
              onClick={openCreateModal}
            >
              + {t('Create Dashboard')}
            </button>
          </div>
        )}
      </div>

      {dashboards.length > 0 ? (
        <div className={styles.tableWrapper}>
          <table className={styles.dashboards}>
            <thead>
              <tr>
                <td>{t('Name')}</td>
                <td>{t('Description')}</td>
                {isSuperAdmin && <td></td>}
              </tr>
            </thead>
            <tbody>
              {dashboards.map((dashboard) => (
                <tr key={dashboard.id}>
                  <td>
                    <Link to={`/custom-dashboards/${dashboard.id}`}>
                      {dashboard.title}
                    </Link>
                  </td>
                  <td>{dashboard.description}</td>
                  {isSuperAdmin && (
                    <td>
                      <div className={styles.controls}>
                        <button
                          type="button"
                          className="tableActionBtn"
                          data-tooltip-id="copy-dashboard"
                          data-tooltip-content={t('Copy')}
                          aria-label={`${t('Copy')} ${dashboard.title}`}
                          onClick={() => openCopyModal(dashboard)}
                        >
                          <CopyIcon />
                        </button>
                        <button
                          type="button"
                          className="tableActionBtn"
                          data-tooltip-id="edit-dashboard"
                          data-tooltip-content={t('Edit')}
                          aria-label={`${t('Edit')} ${dashboard.title}`}
                          onClick={() => openEditModal(dashboard)}
                        >
                          <EditIcon />
                        </button>
                        <button
                          type="button"
                          className="tableActionBtn"
                          data-tooltip-id="delete-dashboard"
                          data-tooltip-content={t('Delete')}
                          aria-label={`${t('Delete')} ${dashboard.title}`}
                          onClick={() =>
                            setDeleteConfirmModal({
                              isActive: true,
                              id: dashboard.id,
                            })
                          }
                        >
                          <DeleteIcon />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              <Tooltip id="copy-dashboard" place="bottom" />
              <Tooltip id="edit-dashboard" place="bottom" />
              <Tooltip id="delete-dashboard" place="bottom" />
            </tbody>
          </table>
          {totalDashboards > PAGE_SIZE && (
            <Paginator
              id={undefined}
              scrollContainerId={undefined}
              page={currentPage}
              size={PAGE_SIZE}
              total={totalDashboards}
              onPageChange={handlePageChange as any}
            />
          )}
        </div>
      ) : (
        <div className="noData">
          {t('You have no available dashboards yet')}
        </div>
      )}

      <Modal
        isVisible={formModal.isActive}
        title={isEditMode ? t('Edit Dashboard') : isCopyMode ? t('Copy Dashboard') : t('Create Dashboard')}
        onClose={closeFormModal}
        className=""
        content={
          <div className={styles.formContent}>
            <div className="form-element">
              <label>{t('Title')}</label>
              <input
                type="text"
                placeholder={t('Dashboard title') as string}
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                autoFocus
              />
            </div>
            <div className="form-element">
              <label>{t('Description')}</label>
              <input
                type="text"
                placeholder={t('Description (optional)') as string}
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
              />
            </div>
            {isCopyMode && isSuperAdmin && (
              <div className="form-element">
                <label>
                  {t('Target workspace (leave empty to copy into the current workspace)')}
                </label>
                <Async
                  cacheOptions
                  defaultOptions={defaultWorkspaceOptions}
                  isClearable
                  value={targetWorkspace}
                  onChange={(value) => setTargetWorkspace((value as WorkspaceOption) ?? null)}
                  loadOptions={loadWorkspaceOptions}
                  placeholder={t('Please enter at least 1 symbol to search') as string}
                  noOptionsMessage={() => t('Please enter at least 1 symbol') as string}
                  loadingMessage={() => t('Loading...') as string}
                />
              </div>
            )}
          </div>
        }
        footer={
          <>
            <button className="new-button" onClick={handleFormSubmit}>
              {isEditMode ? t('Save') : isCopyMode ? t('Copy') : t('Create')}
            </button>
            <button className="btn-reset" onClick={closeFormModal}>
              {t('Cancel')}
            </button>
          </>
        }
      />

      <Modal
        isVisible={deleteConfirmModal.isActive}
        title={t('Delete')}
        onClose={() => setDeleteConfirmModal({ isActive: false, id: null })}
        className=""
        content={<p>{t('Are you sure you want to delete this dashboard?')}</p>}
        footer={
          <>
            <button
              className="btn-danger"
              onClick={() => handleDelete(deleteConfirmModal.id)}
            >
              {t('Delete')}
            </button>
            <button
              className="btn-reset"
              onClick={() =>
                setDeleteConfirmModal({ isActive: false, id: null })
              }
            >
              {t('Cancel')}
            </button>
          </>
        }
      />
    </div>
  );
};

export const DashboardsListPage: React.FC = () => {
  return (
    <DashboardProvider>
      <DashboardsListInner />
    </DashboardProvider>
  );
};
